import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup 
} from 'react-bootstrap';
import { 
  FaCalendarAlt, FaPlus, FaEdit, 
  FaTrash, FaSearch 
} from 'react-icons/fa';
import axios from 'axios';
import { format } from 'date-fns';

const PausedDates = () => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentDate, setCurrentDate] = useState({
    _id: null,
    pausedDate: '',
    reason: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDates();
  }, []);

  const fetchDates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/paused-dates`, {
        headers: { 'x-auth-token': token }
      });
      setDates(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (currentDate._id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/paused-dates/${currentDate._id}`,
          currentDate,
          { headers: { 'x-auth-token': token } }
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/paused-dates`,
          currentDate,
          { headers: { 'x-auth-token': token } }
        );
      }
      setShowModal(false);
      fetchDates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save date');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this paused date?')) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`${process.env.REACT_APP_API_URL}/admin/paused-dates/${id}`, {
          headers: { 'x-auth-token': token }
        });
        fetchDates();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete date');
      }
    }
  };

  const filteredDates = dates.filter(date => 
    date.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
    date.pausedDate.includes(searchTerm)
  );

  return (
    <div className="paused-dates-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FaCalendarAlt className="me-2" />
          Paused Dates Management
        </h2>
        <Button onClick={() => {
          setCurrentDate({ _id: null, pausedDate: '', reason: '' });
          setShowModal(true);
        }}>
          <FaPlus className="me-2" />
          Add New Date
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by date or reason"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date</th>
              <th>Reason</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDates.map(date => (
              <tr key={date._id}>
                <td>{format(new Date(date.pausedDate), 'PPP')}</td>
                <td>{date.reason}</td>
                <td>
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    onClick={() => {
                      setCurrentDate(date);
                      setShowModal(true);
                    }}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(date._id)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentDate._id ? 'Edit Paused Date' : 'Add New Paused Date'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={currentDate.pausedDate}
                onChange={(e) => setCurrentDate({
                  ...currentDate,
                  pausedDate: e.target.value
                })}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={currentDate.reason}
                onChange={(e) => setCurrentDate({
                  ...currentDate,
                  reason: e.target.value
                })}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default PausedDates;